import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, Loader2, CheckCircle2 } from 'lucide-react';
import { COLORS } from '../data/mockData';
import {
  HIPAA_DISCLAIMER, HIPAA_POLICY_SUMMARY, HIPAA_POLICY_VERSION,
} from '../data/hipaaPolicy';
import { submitPhiConsent } from '../services/api';
import { humanizeApiError } from '../lib/errors';

/**
 * HIPAA disclaimer & privacy-policy consent dialog.
 * Members must agree before downloading medical templates or uploading
 * medical information. Agreement is recorded server-side per policy version.
 */
export default function HipaaConsentModal({ open, onAgreed, onClose }) {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const handleAgree = async () => {
    setError('');
    setSubmitting(true);
    try {
      const res = await submitPhiConsent(HIPAA_POLICY_VERSION);
      onAgreed?.(res);
    } catch (e) {
      setError(humanizeApiError(e, 'Could not record your agreement'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 400, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div data-testid="hipaa-consent-modal" style={{ backgroundColor: '#FFF', borderRadius: 20, padding: 28, maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: `${COLORS.primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ShieldCheck size={24} color={COLORS.primary} />
          </div>
          <div>
            <h3 style={{ fontSize: 20, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', margin: 0 }}>
              HIPAA Disclaimer & Privacy Policy
            </h3>
            <div style={{ fontSize: 12, color: COLORS.textSecondary }}>Policy version {HIPAA_POLICY_VERSION}</div>
          </div>
        </div>

        <div data-testid="hipaa-disclaimer-text" style={{ backgroundColor: '#FFF8E1', border: '1px solid #F0E0A0', borderRadius: 12, padding: 14, fontSize: 13, color: COLORS.textPrimary, lineHeight: 1.6, marginBottom: 16 }}>
          <strong style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <AlertTriangle size={14} color="#A65500" /> Disclaimer
          </strong>
          {HIPAA_DISCLAIMER}
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 8, fontFamily: 'Outfit, sans-serif' }}>
            How ThriveLonger.ai safeguards your records:
          </div>
          <ul style={{ margin: 0, paddingLeft: 4, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {HIPAA_POLICY_SUMMARY.map((line, i) => (
              <li key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.5 }}>
                <CheckCircle2 size={15} color={COLORS.success} style={{ flexShrink: 0, marginTop: 2 }} />
                {line}
              </li>
            ))}
          </ul>
          <p style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 10 }}>
            Read the full policy in{' '}
            <Link to="/about#hipaa" target="_blank" rel="noopener noreferrer" style={{ color: COLORS.primary, fontWeight: 600 }}>
              About → HIPAA Compliance &amp; Data Protection
            </Link>.
          </p>
        </div>

        <label data-testid="hipaa-agree-checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: COLORS.textPrimary, cursor: 'pointer', backgroundColor: COLORS.surfaceSecondary, borderRadius: 10, padding: 12, marginBottom: 16 }}>
          <input type="checkbox" data-testid="hipaa-agree-checkbox" checked={checked}
            onChange={e => setChecked(e.target.checked)} style={{ marginTop: 2 }} />
          <span>
            I have read the disclaimer and ThriveLonger.ai's HIPAA compliance policy, and I
            agree to encrypt and store my medical information on this platform.
          </span>
        </label>

        {error && (
          <div data-testid="hipaa-consent-error" style={{ color: COLORS.error, fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={14} /> {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          {onClose && (
            <button data-testid="hipaa-decline-btn" onClick={onClose}
              style={{ flex: 1, padding: '13px', borderRadius: 10, border: `1px solid ${COLORS.border}`, backgroundColor: '#FFF', color: COLORS.textSecondary, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
              Not now
            </button>
          )}
          <button data-testid="hipaa-agree-btn" onClick={handleAgree} disabled={!checked || submitting}
            style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px', borderRadius: 10, border: 'none',
                     backgroundColor: checked ? COLORS.primary : COLORS.border, color: '#FFF', fontWeight: 700, fontSize: 15,
                     cursor: checked && !submitting ? 'pointer' : 'not-allowed', opacity: submitting ? 0.7 : 1 }}>
            {submitting ? <Loader2 size={16} className="spin" /> : <ShieldCheck size={16} />}
            I Agree — Continue
          </button>
        </div>
      </div>
    </div>
  );
}
